import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Shield
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SiteLogo from '../../components/Layout/SiteLogo';
import LanguageSwitcher from '../../components/Layout/LanguageSwitcher';
import { useSettings } from '../../contexts/SettingsContext';
import {
  buildReviewSummary,
  DOCUMENT_VALUES,
  emptyResponses,
  ResidenceResponses,
  WIZARD_STEPS,
  WizardStepId,
  YesNo
} from './residenceAnalysisFormHelpers';

const radioClass = 'h-5 w-5 shrink-0 text-primary-600 focus:ring-primary-500';

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required
}) => (
  <span className="visa-form-label">
    {children}
    {required && <span className="text-red-500"> *</span>}
  </span>
);

const YesNoGroup: React.FC<{
  name: string;
  value: YesNo;
  onChange: (v: YesNo) => void;
  label: string;
  yesLabel: string;
  noLabel: string;
}> = ({ name, value, onChange, label, yesLabel, noLabel }) => (
  <fieldset className="visa-form-field space-y-3">
    <legend className="visa-form-label mb-0">{label}</legend>
    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
      {(['sim', 'nao'] as const).map((opt) => (
        <label
          key={opt}
          className="inline-flex items-center gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 min-h-[52px] sm:min-h-[44px] sm:rounded-lg sm:border sm:px-3 sm:py-2.5 sm:bg-transparent has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50"
        >
          <input
            type="radio"
            name={name}
            checked={value === opt}
            onChange={() => onChange(opt)}
            className={radioClass}
          />
          <span className="text-base sm:text-sm font-medium text-gray-900">{opt === 'sim' ? yesLabel : noLabel}</span>
        </label>
      ))}
    </div>
  </fieldset>
);

const RadioGroup: React.FC<{
  name: string;
  legend: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  optionLabel: (v: string) => string;
}> = ({ name, legend, value, onChange, options, optionLabel }) => (
  <fieldset className="visa-form-field space-y-3">
    <legend className="visa-form-label mb-0">{legend}</legend>
    <div className="flex flex-col gap-2.5">
      {options.map((opt) => (
        <label
          key={opt}
          className="inline-flex items-center gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 min-h-[52px] sm:min-h-0 sm:rounded-lg sm:border sm:px-3 sm:py-2.5 sm:bg-transparent has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50"
        >
          <input
            type="radio"
            name={name}
            className={radioClass}
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          <span className="text-base sm:text-sm font-medium text-gray-900">{optionLabel(opt)}</span>
        </label>
      ))}
    </div>
  </fieldset>
);

const CheckboxGroup: React.FC<{
  legend: string;
  values: string[];
  onChange: (v: string[]) => void;
  options: readonly string[];
  optionLabel: (v: string) => string;
}> = ({ legend, values, onChange, options, optionLabel }) => (
  <fieldset className="visa-form-field space-y-3">
    <legend className="visa-form-label mb-0">{legend}</legend>
    <div className="flex flex-col gap-2.5">
      {options.map((opt) => (
        <label
          key={opt}
          className="inline-flex items-center gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 min-h-[52px] sm:min-h-0 sm:rounded-lg sm:border sm:px-3 sm:py-2.5 sm:bg-transparent has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50"
        >
          <input
            type="checkbox"
            className="h-5 w-5 shrink-0 rounded text-primary-600 focus:ring-primary-500"
            checked={values.includes(opt)}
            onChange={(e) =>
              onChange(e.target.checked ? [...values, opt] : values.filter((v) => v !== opt))
            }
          />
          <span className="text-base sm:text-sm font-medium text-gray-900">{optionLabel(opt)}</span>
        </label>
      ))}
    </div>
  </fieldset>
);

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}> = ({ label, value, onChange, type = 'text', required, placeholder, inputMode }) => (
  <label className="visa-form-field block">
    <FieldLabel required={required}>{label}</FieldLabel>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      className="visa-form-input"
    />
  </label>
);

const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
}> = ({ label, value, onChange, required, placeholder }) => (
  <label className="visa-form-field block">
    <FieldLabel required={required}>{label}</FieldLabel>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={6}
      className="visa-form-input"
    />
  </label>
);

const stepFieldsClass = 'space-y-5 sm:space-y-4 min-w-0';

const pageShell = 'min-h-[100dvh] bg-slate-100';

const ResidenceAnalysisForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const tf = (key: string) => t(`residenceForm.${key}`);
  const fld = (key: string) => t(`residenceForm.fields.${key}`);
  const yesLabel = t('common.yes');
  const noLabel = t('common.no');
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState<ResidenceResponses>(emptyResponses());
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [stepError, setStepError] = useState('');

  const steps = WIZARD_STEPS;
  const currentStep = steps[stepIndex] ?? 'personal';
  const progress = steps.length > 1 ? ((stepIndex + 1) / steps.length) * 100 : 0;
  const displaySiteName = settings?.site?.name || siteName || 'NEIA';
  const reviewSections = useMemo(() => buildReviewSummary(data, t), [data, t, i18n.language]);

  useEffect(() => {
    if (!token) {
      setErrorMsg(tf('linkInvalid'));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/residence-analysis/public/${token}`);
        setValid(res.data.valid);
        setAlreadySubmitted(res.data.alreadySubmitted);
        setSiteName(res.data.siteName || 'NEIA');
      } catch (err: any) {
        setValid(false);
        setErrorMsg(err.response?.data?.message || tf('linkExpired'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, t]);

  const patch = useCallback(
    <K extends keyof ResidenceResponses>(section: K, field: keyof ResidenceResponses[K], value: unknown) => {
      setData((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    },
    []
  );

  const validateStep = (step: WizardStepId): string | null => {
    const err = (key: string) => t(`residenceForm.errors.${key}`);
    if (step === 'personal') {
      if (!data.personal.fullName.trim() || data.personal.fullName.trim().length < 3) {
        return err('fullName');
      }
      if (!data.personal.nationality.trim()) return err('nationality');
      if (!data.personal.age.trim() || Number.isNaN(Number(data.personal.age)) || Number(data.personal.age) <= 0) {
        return err('age');
      }
    }
    if (step === 'situation') {
      if (!data.currentSituation.inPortugal) return err('inPortugal');
      if (data.currentSituation.inPortugal === 'sim') {
        if (!data.currentSituation.since.trim()) return err('since');
        if (!data.currentSituation.howEntered) return err('howEntered');
        if (data.currentSituation.howEntered === 'outra' && !data.currentSituation.howEnteredOther.trim()) {
          return err('howEnteredOther');
        }
      }
      if (!data.currentSituation.hadResidencyBefore) return err('hadResidencyBefore');
    }
    if (step === 'workStudy') {
      if (!data.workStudy.currentlyWorking) return err('currentlyWorking');
      if (!data.workStudy.hasContractOrActivity) return err('hasContractOrActivity');
      if (!data.workStudy.studiedInPortugal) return err('studiedInPortugal');
      if (data.workStudy.studiedInPortugal !== 'nao') {
        if (!data.workStudy.studyLevel) return err('studyLevel');
        if (data.workStudy.studyLevel === 'outro' && !data.workStudy.studyLevelOther.trim()) {
          return err('studyLevelOther');
        }
      }
    }
    if (step === 'family') {
      if (!data.family.hasChildren) return err('hasChildren');
      if (data.family.hasChildren === 'sim') {
        if (!data.family.childUnder18) return err('childUnder18');
        if (!data.family.childInPortugal) return err('childInPortugal');
        if (!data.family.childPortugueseNationality) return err('childPortugueseNationality');
      }
      if (!data.family.hasCloseRelativeWithStatus) return err('hasCloseRelativeWithStatus');
      if (data.family.hasCloseRelativeWithStatus === 'sim' && !data.family.relationshipDegree.trim()) {
        return err('relationshipDegree');
      }
    }
    if (step === 'special') {
      const s = data.specialSituations;
      if (
        !s.bornInPortugal ||
        !s.hasHealthCondition ||
        !s.hadResidencyExpiredAndStayed ||
        !s.wasVictimOfExploitation ||
        !s.hadStudentOrHighlyQualifiedResidency ||
        !s.hadInternationalProtection ||
        !s.hasInvestmentOrCompany
      ) {
        return err('special');
      }
    }
    if (step === 'history') {
      const h = data.migratoryHistory;
      if (!h.hadRequestDenied || !h.hadDeportationOrExpulsionOrder || !h.wasEverIrregular) {
        return err('history');
      }
    }
    if (step === 'documents') {
      if (data.documents.items.includes('outros') && !data.documents.otherDescription.trim()) {
        return err('documentsOther');
      }
    }
    if (step === 'description') {
      if (!data.description.situationDescription.trim() || data.description.situationDescription.trim().length < 10) {
        return err('situationDescription');
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(currentStep);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError('');
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => {
    setStepError('');
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = async () => {
    for (const step of steps) {
      if (step === 'review') continue;
      const err = validateStep(step);
      if (err) {
        setStepError(err);
        return;
      }
    }
    setSubmitting(true);
    setStepError('');
    try {
      await axios.post(`/residence-analysis/public/${token}/submit`, data);
      setDone(true);
    } catch (e: any) {
      const msg =
        e.response?.data?.errors?.[0]?.msg ||
        e.response?.data?.message ||
        tf('submitError');
      setStepError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'personal':
        return (
          <div className={stepFieldsClass}>
            <TextField
              label={fld('fullName')}
              value={data.personal.fullName}
              onChange={(v) => patch('personal', 'fullName', v)}
              required
            />
            <TextField
              label={fld('nationality')}
              value={data.personal.nationality}
              onChange={(v) => patch('personal', 'nationality', v)}
              required
            />
            <TextField
              label={fld('age')}
              type="number"
              inputMode="numeric"
              value={data.personal.age}
              onChange={(v) => patch('personal', 'age', v)}
              required
            />
          </div>
        );
      case 'situation':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup
              name="inPortugal"
              label={fld('inPortugal')}
              value={data.currentSituation.inPortugal}
              onChange={(v) => patch('currentSituation', 'inPortugal', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            {data.currentSituation.inPortugal === 'sim' && (
              <>
                <TextField
                  label={fld('since')}
                  value={data.currentSituation.since}
                  onChange={(v) => patch('currentSituation', 'since', v)}
                  required
                />
                <RadioGroup
                  name="howEntered"
                  legend={fld('howEntered')}
                  value={data.currentSituation.howEntered}
                  onChange={(v) => patch('currentSituation', 'howEntered', v)}
                  options={['turismo_isencao_visto', 'visto', 'outra']}
                  optionLabel={(v) => t(`residenceForm.howEntered.${v}`)}
                />
                {data.currentSituation.howEntered === 'outra' && (
                  <TextField
                    label={fld('howEnteredOther')}
                    value={data.currentSituation.howEnteredOther}
                    onChange={(v) => patch('currentSituation', 'howEnteredOther', v)}
                    required
                  />
                )}
              </>
            )}
            <RadioGroup
              name="hadResidencyBefore"
              legend={fld('hadResidencyBefore')}
              value={data.currentSituation.hadResidencyBefore}
              onChange={(v) => patch('currentSituation', 'hadResidencyBefore', v)}
              options={['nunca', 'sim_valida', 'sim_caducada', 'sim_cancelada']}
              optionLabel={(v) => t(`residenceForm.hadResidencyBefore.${v}`)}
            />
          </div>
        );
      case 'workStudy':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup
              name="currentlyWorking"
              label={fld('currentlyWorking')}
              value={data.workStudy.currentlyWorking}
              onChange={(v) => patch('workStudy', 'currentlyWorking', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            <YesNoGroup
              name="hasContractOrActivity"
              label={fld('hasContractOrActivity')}
              value={data.workStudy.hasContractOrActivity}
              onChange={(v) => patch('workStudy', 'hasContractOrActivity', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            <RadioGroup
              name="studiedInPortugal"
              legend={fld('studiedInPortugal')}
              value={data.workStudy.studiedInPortugal}
              onChange={(v) => patch('workStudy', 'studiedInPortugal', v)}
              options={['nao', 'estudando', 'concluido']}
              optionLabel={(v) => t(`residenceForm.studiedInPortugal.${v}`)}
            />
            {data.workStudy.studiedInPortugal && data.workStudy.studiedInPortugal !== 'nao' && (
              <>
                <RadioGroup
                  name="studyLevel"
                  legend={fld('studyLevel')}
                  value={data.workStudy.studyLevel}
                  onChange={(v) => patch('workStudy', 'studyLevel', v)}
                  options={['secundario', 'profissional', 'licenciatura', 'mestrado', 'doutoramento', 'outro']}
                  optionLabel={(v) => t(`residenceForm.studyLevel.${v}`)}
                />
                {data.workStudy.studyLevel === 'outro' && (
                  <TextField
                    label={fld('studyLevelOther')}
                    value={data.workStudy.studyLevelOther}
                    onChange={(v) => patch('workStudy', 'studyLevelOther', v)}
                    required
                  />
                )}
              </>
            )}
          </div>
        );
      case 'family':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup
              name="hasChildren"
              label={fld('hasChildren')}
              value={data.family.hasChildren}
              onChange={(v) => patch('family', 'hasChildren', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            {data.family.hasChildren === 'sim' && (
              <>
                <YesNoGroup
                  name="childUnder18"
                  label={fld('childUnder18')}
                  value={data.family.childUnder18}
                  onChange={(v) => patch('family', 'childUnder18', v)}
                  yesLabel={yesLabel}
                  noLabel={noLabel}
                />
                <YesNoGroup
                  name="childInPortugal"
                  label={fld('childInPortugal')}
                  value={data.family.childInPortugal}
                  onChange={(v) => patch('family', 'childInPortugal', v)}
                  yesLabel={yesLabel}
                  noLabel={noLabel}
                />
                <YesNoGroup
                  name="childPortugueseNationality"
                  label={fld('childPortugueseNationality')}
                  value={data.family.childPortugueseNationality}
                  onChange={(v) => patch('family', 'childPortugueseNationality', v)}
                  yesLabel={yesLabel}
                  noLabel={noLabel}
                />
              </>
            )}
            <YesNoGroup
              name="hasCloseRelativeWithStatus"
              label={fld('hasCloseRelativeWithStatus')}
              value={data.family.hasCloseRelativeWithStatus}
              onChange={(v) => patch('family', 'hasCloseRelativeWithStatus', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            {data.family.hasCloseRelativeWithStatus === 'sim' && (
              <TextField
                label={fld('relationshipDegree')}
                value={data.family.relationshipDegree}
                onChange={(v) => patch('family', 'relationshipDegree', v)}
                required
              />
            )}
          </div>
        );
      case 'special':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="bornInPortugal" label={fld('bornInPortugal')} value={data.specialSituations.bornInPortugal} onChange={(v) => patch('specialSituations', 'bornInPortugal', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="hasHealthCondition" label={fld('hasHealthCondition')} value={data.specialSituations.hasHealthCondition} onChange={(v) => patch('specialSituations', 'hasHealthCondition', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="hadResidencyExpiredAndStayed" label={fld('hadResidencyExpiredAndStayed')} value={data.specialSituations.hadResidencyExpiredAndStayed} onChange={(v) => patch('specialSituations', 'hadResidencyExpiredAndStayed', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="wasVictimOfExploitation" label={fld('wasVictimOfExploitation')} value={data.specialSituations.wasVictimOfExploitation} onChange={(v) => patch('specialSituations', 'wasVictimOfExploitation', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="hadStudentOrHighlyQualifiedResidency" label={fld('hadStudentOrHighlyQualifiedResidency')} value={data.specialSituations.hadStudentOrHighlyQualifiedResidency} onChange={(v) => patch('specialSituations', 'hadStudentOrHighlyQualifiedResidency', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="hadInternationalProtection" label={fld('hadInternationalProtection')} value={data.specialSituations.hadInternationalProtection} onChange={(v) => patch('specialSituations', 'hadInternationalProtection', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="hasInvestmentOrCompany" label={fld('hasInvestmentOrCompany')} value={data.specialSituations.hasInvestmentOrCompany} onChange={(v) => patch('specialSituations', 'hasInvestmentOrCompany', v)} yesLabel={yesLabel} noLabel={noLabel} />
          </div>
        );
      case 'history':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="hadRequestDenied" label={fld('hadRequestDenied')} value={data.migratoryHistory.hadRequestDenied} onChange={(v) => patch('migratoryHistory', 'hadRequestDenied', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="hadDeportationOrExpulsionOrder" label={fld('hadDeportationOrExpulsionOrder')} value={data.migratoryHistory.hadDeportationOrExpulsionOrder} onChange={(v) => patch('migratoryHistory', 'hadDeportationOrExpulsionOrder', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="wasEverIrregular" label={fld('wasEverIrregular')} value={data.migratoryHistory.wasEverIrregular} onChange={(v) => patch('migratoryHistory', 'wasEverIrregular', v)} yesLabel={yesLabel} noLabel={noLabel} />
          </div>
        );
      case 'documents':
        return (
          <div className={stepFieldsClass}>
            <CheckboxGroup
              legend={fld('documentsItems')}
              values={data.documents.items}
              onChange={(v) => patch('documents', 'items', v)}
              options={DOCUMENT_VALUES}
              optionLabel={(v) => t(`residenceForm.documentsList.${v}`)}
            />
            {data.documents.items.includes('outros') && (
              <TextField
                label={fld('documentsOther')}
                value={data.documents.otherDescription}
                onChange={(v) => patch('documents', 'otherDescription', v)}
                required
              />
            )}
          </div>
        );
      case 'description':
        return (
          <div className={stepFieldsClass}>
            <TextAreaField
              label={fld('situationDescription')}
              value={data.description.situationDescription}
              onChange={(v) => patch('description', 'situationDescription', v)}
              required
            />
          </div>
        );
      case 'review':
        return (
          <div className={`${stepFieldsClass} text-gray-800`}>
            <p className="text-base sm:text-sm text-gray-600 leading-relaxed">{tf('reviewIntro')}</p>
            <div className="rounded-xl border-2 border-gray-100 bg-gray-50 p-4 sm:p-5 space-y-5 max-h-[min(50vh,480px)] sm:max-h-[28rem] overflow-y-auto overflow-x-hidden overscroll-contain">
              {reviewSections.map((section) => (
                <div key={section.title} className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-base mb-2.5">{section.title}</h3>
                  <dl className="space-y-2.5">
                    {section.lines.map((row) => (
                      <div
                        key={`${section.title}-${row.label}`}
                        className="min-w-0 border-b border-gray-200/80 pb-2.5 last:border-0 last:pb-0"
                      >
                        <dt className="text-sm sm:text-xs font-medium text-gray-500 mb-0.5">{row.label}</dt>
                        <dd className="text-base sm:text-sm text-gray-900 font-medium break-words">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{tf('linkUnavailable')}</h1>
          <p className="text-gray-600">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (alreadySubmitted && !done) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{tf('alreadySubmittedTitle')}</h1>
          <p className="text-gray-600">{t('residenceForm.alreadySubmittedMsg', { siteName: displaySiteName })}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className={`flex items-center justify-center p-4 sm:p-6 ${pageShell}`}>
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <CheckCircle className="w-14 h-14 text-primary-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{tf('thankYou')}</h1>
          <p className="text-gray-600">{t('residenceForm.successMsg', { siteName: displaySiteName })}</p>
        </div>
      </div>
    );
  }

  const navButtons = (
    <>
      <button
        type="button"
        onClick={goBack}
        disabled={stepIndex === 0}
        className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1 px-4 py-3 sm:py-2 min-h-[48px] sm:min-h-0 rounded-xl sm:rounded-lg border border-gray-300 text-gray-700 font-medium disabled:opacity-40 touch-manipulation"
      >
        <ChevronLeft className="w-4 h-4 shrink-0" /> {tf('prev')}
      </button>
      {currentStep === 'review' ? (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 px-6 py-3 sm:py-2 min-h-[48px] sm:min-h-0 rounded-xl sm:rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60 touch-manipulation"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {tf('submit')}
        </button>
      ) : (
        <button
          type="button"
          onClick={goNext}
          className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1 px-6 py-3 sm:py-2 min-h-[48px] sm:min-h-0 rounded-xl sm:rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 touch-manipulation"
        >
          {tf('next')} <ChevronRight className="w-4 h-4 shrink-0" />
        </button>
      )}
    </>
  );

  return (
    <div className={`${pageShell} pb-[max(1rem,env(safe-area-inset-bottom))]`}>
      <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-8 lg:py-10">
        <header className="text-center text-gray-900 mb-5 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <SiteLogo
              size="lg"
              nameClassName="font-bold text-gray-900 text-xl sm:text-2xl"
              className="justify-center"
            />
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight px-1">
            {tf('title')}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base mt-2 max-w-lg mx-auto px-2">
            {tf('subtitle')}
          </p>
          <div className="flex justify-center mt-4 lg:hidden">
            <LanguageSwitcher compact variant="floating" className="shadow-md" />
          </div>
        </header>

        <div className="flex flex-col items-stretch lg:flex-row lg:items-start lg:justify-center lg:gap-5 xl:gap-8">
          <div className="w-full max-w-2xl mx-auto lg:mx-0 min-w-0 flex-1">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-full">
              <div className="px-4 pt-4 sm:px-6 sm:pt-6">
                <div className="flex justify-between text-xs sm:text-sm text-gray-500 mb-2">
                  <span>{t('residenceForm.stepOf', { current: stepIndex + 1, total: steps.length })}</span>
                  <span className="font-medium text-primary-600">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div
                  className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 lg:hidden scrollbar-none snap-x snap-mandatory"
                  aria-hidden
                >
                  {steps.map((step, i) => {
                    const active = i === stepIndex;
                    const stepDone = i < stepIndex;
                    return (
                      <div
                        key={step}
                        className={`shrink-0 snap-center w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition-colors ${
                          active
                            ? 'bg-primary-600 text-white'
                            : stepDone
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {i + 1}
                      </div>
                    );
                  })}
                </div>

                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mt-3 sm:mt-4 leading-snug">
                  {t(`residenceForm.steps.${currentStep}`)}
                </h2>
              </div>

              <div className="px-4 py-5 sm:px-6 sm:py-6 overflow-x-hidden min-w-0">
                {renderStep()}
              </div>

              {stepError && (
                <p className="px-4 sm:px-6 pb-2 text-base sm:text-sm font-medium text-red-600" role="alert">
                  {stepError}
                </p>
              )}

              <div className="hidden sm:flex px-6 pb-6 justify-between gap-3">{navButtons}</div>
            </div>

            <div
              className="fixed inset-x-0 bottom-0 z-30 sm:hidden border-t border-gray-200/90 bg-white/95 backdrop-blur-md px-4 pt-3 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            >
              <div className="flex gap-3 max-w-2xl mx-auto">{navButtons}</div>
            </div>
            <div className="h-[5.5rem] sm:hidden" aria-hidden />

            <p className="text-center text-gray-500 text-xs sm:text-sm mt-4 sm:mt-6 px-2 pb-24 sm:pb-0">
              {tf('privacy')}
            </p>
          </div>

          <aside
            className="hidden lg:flex shrink-0 sticky top-8 self-start flex-col items-center"
            aria-label={t('residenceForm.languagePanel', { defaultValue: 'Idioma' })}
          >
            <LanguageSwitcher orientation="vertical" variant="floating" compact />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ResidenceAnalysisForm;
